import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";
import DateField from "./tax/DateField";
import { saveDataEntry, upsertCompany } from "../lib/api";

const emptyForm = {
  name: "", coNo: "", taxC: "", employerE: "", businessCode: "",
  ya: "2025", accFrom: "", accTo: "", basisFrom: "", basisTo: "",
  commencementDate: "", operationStatus: "In Operation",
};

const statusMap = { "In Operation": "Active", "Dormant": "Dormant", "Winding Up": "Winding Up" };

// Shared "New / Edit Company" dialog used by both the Client Listing (Dashboard) and Company list.
const CompanyFormDialog = ({ open, onOpenChange, company, onSaved }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState(emptyForm);
  const editingId = company?.id || null;

  useEffect(() => {
    if (!open) return;
    if (company) {
      setForm({
        name: company.name || "",
        coNo: company.coNo || company.regNo || "",
        taxC: company.taxC || "",
        employerE: company.employerE || "",
        businessCode: company.businessCode || "",
        ya: company.ya || "2025",
        accFrom: company.accFrom || "",
        accTo: company.accTo || "",
        basisFrom: company.basisFrom || "",
        basisTo: company.basisTo || "",
        commencementDate: company.commencementDate || "",
        operationStatus: company.operationStatus || "In Operation",
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, company]);

  const buildPayload = () => ({
    id: editingId || undefined,
    name: form.name,
    coNo: form.coNo,
    regNo: form.coNo,
    taxC: form.taxC,
    employerE: form.employerE,
    businessCode: form.businessCode,
    ya: form.ya || "2025",
    accFrom: form.accFrom,
    accTo: form.accTo,
    basisFrom: form.basisFrom,
    basisTo: form.basisTo,
    commencementDate: form.commencementDate,
    operationStatus: form.operationStatus,
    status: statusMap[form.operationStatus] || "Active",
    form: "C",
    filingStatus: "Draft",
    refNo: form.taxC || (form.coNo ? `C ${form.coNo}` : ""),
  });

  const saveCompany = async (proceed) => {
    if (!form.name || !form.coNo) {
      toast({ title: "Please fill company name and company number.", variant: "destructive" });
      return;
    }
    let record;
    try {
      record = await upsertCompany(buildPayload());
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Could not save company.", variant: "destructive" });
      return;
    }
    try {
      await saveDataEntry(record.id, {
        name: record.name, companyNo: record.coNo, taxFileNo: record.taxC,
        businessCode: record.businessCode, employerE: record.employerE, year: record.ya,
        accFrom: record.accFrom, accTo: record.accTo,
        basisFrom: record.basisFrom, basisTo: record.basisTo,
        commencementDate: record.commencementDate, operationStatus: record.operationStatus,
      });
    } catch (e) { /* non-blocking */ }
    onOpenChange(false);
    if (onSaved) onSaved();
    if (proceed) {
      navigate(`/company/${record.id}/tax`);
    } else {
      toast({ title: editingId ? "Company updated" : "Company added", description: record.name });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingId ? "Edit Company" : "New Company"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Company Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Acme Sdn Bhd" data-testid="company-form-name" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Company Number</Label>
              <Input value={form.coNo} onChange={(e) => setForm({ ...form, coNo: e.target.value })} placeholder="e.g. 202301001234" />
            </div>
            <div className="space-y-2">
              <Label>Tax C Number</Label>
              <Input value={form.taxC} onChange={(e) => setForm({ ...form, taxC: e.target.value })} placeholder="e.g. C 25101234567" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Employer E Number</Label>
              <Input value={form.employerE} onChange={(e) => setForm({ ...form, employerE: e.target.value })} placeholder="e.g. E 1234567890" />
            </div>
            <div className="space-y-2">
              <Label>Business Code (B/CODE)</Label>
              <Input value={form.businessCode} onChange={(e) => setForm({ ...form, businessCode: e.target.value })} placeholder="e.g. 73100" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Year of Assessment</Label>
            <Input value={form.ya} onChange={(e) => setForm({ ...form, ya: e.target.value })} placeholder="e.g. 2025" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Accounting Date From</Label>
              <DateField value={form.accFrom} onChange={(v) => setForm({ ...form, accFrom: v })} />
            </div>
            <div className="space-y-2">
              <Label>Accounting Date To</Label>
              <DateField value={form.accTo} onChange={(v) => setForm({ ...form, accTo: v })} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Basis Period From</Label>
              <DateField value={form.basisFrom} onChange={(v) => setForm({ ...form, basisFrom: v })} />
            </div>
            <div className="space-y-2">
              <Label>Basis Period To</Label>
              <DateField value={form.basisTo} onChange={(v) => setForm({ ...form, basisTo: v })} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Commencement Date</Label>
              <DateField value={form.commencementDate} onChange={(v) => setForm({ ...form, commencementDate: v })} />
            </div>
            <div className="space-y-2">
              <Label>Operation Status</Label>
              <Select value={form.operationStatus} onValueChange={(v) => setForm({ ...form, operationStatus: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="In Operation">In Operation</SelectItem>
                  <SelectItem value="Dormant">Dormant</SelectItem>
                  <SelectItem value="Winding Up">Winding Up</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Form Type</Label>
            <div className="flex items-center h-10 px-3 rounded border border-gray-200 bg-gray-50 text-sm text-gray-600">
              Form C (Company)
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="outline" onClick={() => saveCompany(false)} className="border-[#1877c9] text-[#1877c9] hover:bg-[#eef4fb]" data-testid="company-form-save">Save</Button>
          <Button onClick={() => saveCompany(true)} className="bg-[#1877c9] hover:bg-[#0f5ea3]">Save &amp; Tax Computation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CompanyFormDialog;
